import { useMediaQuery, useTheme } from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridTreeNodeWithRender,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import useMassUpdateTasks from 'api/aws/tasks/useMassUpdateTasks'
import { useUserItems } from 'api/aws/users/useUserItems'
import { checkSearchHealth, useEntitiesFromSearch } from 'api/search'
import DrawerWrapper from 'components/DataGrid/DrawerWrapper'
import TaskFooterWrapper from 'components/DataGrid/Footer/TaskFooterWrapper'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { dynamicColumnWidths } from 'components/DataGrid/operators/columnWidths'
import { loadModel } from 'components/DataGrid/operators/loadModels'
import { useProcessTaskRowUpdate } from 'components/DataGrid/operators/useProcessTaskRowUpdate'
import { taskGridSxProps } from 'components/DataGrid/taskGridSxProps'
import TasksGridFooter from 'components/DataGrid/TasksGridFooter'
import { useTaskGridUpdates } from 'components/DataGrid/useTaskGridUpdates'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import TaskDrawer from 'components/TaskDrawer/TaskDrawer'
import { getTeamTaskClassNames } from 'components/TeamTasks/grid/getTeamTaskClassNames'
import renderTeamTasksColumns from 'components/TeamTasks/grid/renderTeamTasksColumns'
import { useDrawerContext } from 'context/DrawerProvider/DrawerProvider'
import { useProjectDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useBuildTaskTreeRows } from 'context/useBuildTaskTreeRows'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import { Subtask, Task, TreeRow } from 'types'
import {
  defaultColumnOrder,
  defaultSortModel,
  defaultVisibilityModel,
  defaultWidths,
  filter_model_projects,
} from './defaultConfigs'

const ProjectsPage = () => {
  const { PK, SK } = useParams()
  const theme = useTheme()
  const _filters = {}
  const apiRef = useGridApiRef()
  const { openProjectDrawer, closeProjectDrawer } = useDrawerContext()
  const isDrawerOpen = useProjectDrawerOpen()
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()
  const { buildTaskTreeRows } = useBuildTaskTreeRows()
  const { isExpanded, toggleExpanded } = useTreeExpansionPersistence('projects')
  const { handleMassUpdate, isUpdating: _isUpdating } = useMassUpdateTasks('projects')
  const [isMassDeleteOpen, setIsMassDeleteOpen] = useState(false)
  const [footerPosition, setFooterPosition] = useState<{ top: number; left: number } | null>(null)
  const footerRef = useRef<HTMLDivElement | null>(null)
  const {
    organizations,
    deals,
    products,
    allTasks,
    commentsTable: comments,
    isItemsLoading,
    isItemsFetching,
  } = useEntitiesFromSearch()
  const [selectedTask, setSelectedTask] = useState<Task>(null)
  const [rowTasks, setRowTasks] = useState<Task[]>([])
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [selectedMassData, setSelectedMassData] = useState<Task[]>([])
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const [customView, setCustomView] = useState<string>(() => {
    return loadModel<string>('projects', 'CustomView', 'default')
  })
  const [searchAvailable, setSearchAvailable] = useState<boolean | null>(null)
  const [syncStatus, setSyncStatus] = useState<{
    documentCount?: number
    lastSyncTime?: string
    isInitialized?: boolean
  }>({})
  const { processTaskRowUpdate, handleHighPrioChange, handlePostdatedChange } = useProcessTaskRowUpdate()
  const processRowUpdate = (newRow: Task, oldRow: Task, rowSelectionModel: GridRowSelectionModel) => {
    return processTaskRowUpdate(newRow, oldRow, rowSelectionModel)
  }
  const isXL = useMediaQuery(theme.breakpoints.up('xl'))
  const isLG = useMediaQuery(theme.breakpoints.up('lg'))
  const isMD = useMediaQuery(theme.breakpoints.up('md'))
  const isSM = useMediaQuery(theme.breakpoints.up('sm'))

  const screenSize = isXL ? 'xl' : isLG ? 'lg' : isMD ? 'md' : isSM ? 'sm' : 'xs'
  const {
    handleSendForReview,
    handleDeleteClick,
    handleClickMassDelete,
    handleConfirmDelete,
    handleMassDelete,
    handleMassUpdateChange,
    massUpdateData,
    setMassUpdateData,
  } = useTaskGridUpdates({
    tasks: rowTasks,
    setTasks: setRowTasks,
    setIsMassDeleteOpen,
    setAnchorEl,
    apiRef,
  })

  //   const [density, setDensity] = useState<GridDensity>(() => {
  //     const saved = localStorage.getItem('projectsDensityModel') as GridDensity | null
  //     return saved ?? 'standard'
  //   })
  const [density, setDensity] = useState<GridDensity>('standard')
  const project_tasks = useMemo(() => {
    if (!allTasks?.length) return []
    const tasks = allTasks.filter(
      t => !t?.SK?.startsWith('MONTHLY_') && !t?.isMarketing && !t?.SK?.startsWith('PROGRESS_')
    )
    return tasks?.map(t => ({ ...t, rowId: `${t.PK}::${t.SK}` }))
  }, [allTasks])
  const treeRows: TreeRow[] = useMemo(() => {
    return buildTaskTreeRows(project_tasks, organizations, comments)
  }, [project_tasks, organizations, comments])

  useEffect(() => {
    const checkHealth = async () => {
      const result = await checkSearchHealth()
      setSearchAvailable(result.available)
      // Extract sync status from health response
      setSyncStatus({
        documentCount: result.documentCount,
        lastSyncTime: result.lastSyncTime,
        isInitialized: result.isInitialized,
      })
      if (!result.available) {
        console.warn('[OrganizationsPageServerSide] Search not available:', result.message)
      }
    }

    checkHealth()

    // Refresh sync status every 30 seconds
    const interval = setInterval(checkHealth, 30000)
    return () => clearInterval(interval)
  }, [])

  /////////////////////////////////////////////////////////////////////// Handle Functions //////////////////////////////////////////////////////////////////////
  const handleTaskClick = (_: any, taskData: Task) => {
    setSelectedTask(taskData)
    // openDrawer(taskData)
    navigate(`/projects/${taskData.PK}/${taskData.SK}/main`)
    // handleClosePopover()
  }
  const handleSubtaskClick = (_: any, taskData: Task) => {
    setSelectedTask(taskData)
    // openDrawer(taskData)
    navigate(`/projects/${taskData.PK}/${taskData.parentSK}/subtasks`)
    // handleClosePopover()
  }
  const handleClickOrganization = (PK: string) => {
    if (!PK) return
    navigate(PK ? `/organizations/${PK}/details` : '#')
  }
  const clickEditStart = (params: GridRenderCellParams) => {
    // again, use the Set’s size
    if (apiRef?.current?.state?.rowSelection.ids.size <= 1) {
      params.api.startCellEditMode({
        id: params.id,
        field: params.field,
      })
    }
  }
  const handleTaskRowModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    // console.log('rowSelectionModel', rowSelectionModel)
    // Extract PK::SK
    const parsed = Array.from(rowSelectionModel?.ids).map(id => {
      const [PK, SK] = (id as string).split('::')
      return { id, PK, SK, isSubtask: SK.startsWith('SUBTASK_') }
    })
    // console.log('parsed', parsed)
    const selectedParents = new Set(parsed.filter(p => !p.isSubtask).map(p => p.PK))
    const selectedSubtasks = new Set(parsed.filter(p => p.isSubtask).map(p => p.SK))

    // Rule: If a parent task is selected, remove its subtasks
    const filteredSelection = parsed
      .filter(p => {
        if (p.isSubtask && selectedParents.has(p.PK)) {
          return false // skip subtask if parent is also selected
        }
        return true
      })
      .map(p => p.id)
    // setRowSelectionModel({ ids: new Set(filteredSelection), type: 'include' })

    // Preserve your existing logic for setting selectedTask when only one is selected
    if (filteredSelection.length === 1) {
      const selectedRowId = filteredSelection[0] as string
      const [_pk, sk] = selectedRowId.split('::')

      if (sk.startsWith('SUBTASK_')) {
        const parentTask = project_tasks.find(task => task.subtasks?.some((sub: Subtask) => sub.SK === sk))
        if (parentTask) {
          const selectedSubtask = parentTask.subtasks.find((sub: Subtask) => sub.SK === sk)
          if (selectedSubtask) {
            const subtaskWithParentAssignees: Subtask = {
              ...selectedSubtask,
              parentSK: parentTask.SK,
              assignees: parentTask.assignees ?? [],
            }
            setSelectedTask(subtaskWithParentAssignees)
          }
        }
      } else {
        const selected = project_tasks.find(task => task.SK === sk)
        if (selected) {
          setSelectedTask(selected)
        }
      }
    } else {
      setSelectedTask(null)
    }
  }
  useEffect(() => {
    if (!footerRef.current) return

    const el = footerRef.current
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect()
      setFooterPosition({ top: rect.top - 12, left: rect.left + rect.width / 2 })
    })

    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const clearSelections = () => {
    setCustomView('default')
    setMassUpdateData({})
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const isCellEditable = (
    params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
    rowSelectionModel: GridRowSelectionModel
  ) => {
    // Check if the column is `invoiceStatus`
    if (params.field === 'masterStatus') {
      // Allow editing only if one or no rows are selected
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    if (params.field === 'highPrio') {
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    if (params.field === 'startDate') {
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    if (params.field === 'endDate') {
      return Array.from(rowSelectionModel?.ids).length <= 1
    }
    return true // Default for other columns
  }
  const updateCommentCount = (taskSK: string, count: number) => {
    setRowTasks(prev =>
      prev.map(task => {
        if (task.SK === taskSK) {
          // ✅ Only update if the count has changed
          if (task.commentCount !== count) {
            return { ...task, commentCount: count }
          }
        }
        return task
      })
    )
  }

  const columns = useMemo(() => {
    return renderTeamTasksColumns(
      organizations,
      users,
      'projects',
      handleClickOrganization,
      density,
      handleTaskClick,
      clickEditStart,
      handleHighPrioChange,
      customView,
      handlePostdatedChange
    )
  }, [
    organizations,
    users,
    handleClickOrganization,
    theme,
    density,
    handleTaskClick,
    handleSubtaskClick,
    clickEditStart,
    handleHighPrioChange,
    handlePostdatedChange,
    handleSendForReview,
    customView,
    isExpanded,
    toggleExpanded,
  ]) as GridColDef[]
  const defaultColumnWidths = useMemo(() => {
    return dynamicColumnWidths(screenSize, defaultWidths, columns)
  }, [columns, screenSize])
  const changeColumnVisibility = (newModel: GridColumnVisibilityModel) => {
    if (newModel?.minimalist) setCustomView('minimalist')
    else if (newModel?.textOnly) setCustomView('textOnly')
    else if (newModel?.websiteAccess) setCustomView('websiteAccess')
  }
  const clearGridSelectionNow = () => {
    // 1) Clear selection immediately
    apiRef?.current?.setRowSelectionModel({ ids: new Set<string>(), type: 'include' })
    // 2) Also clear any focused cell to avoid focus highlight flash
    // const active = document.activeElement as HTMLElement | null
    // if (active && typeof active.blur === 'function') active.blur()
  }

  const closeTaskDrawer = React.useCallback(() => {
    flushSync(() => {
      clearGridSelectionNow()
      setSelectedTask(null)
    })
    // closeProjectDrawer()
    navigate('/projects', { replace: true })
  }, [closeProjectDrawer, navigate])
  const onChange = React.useCallback(handleMassUpdateChange, [handleMassUpdateChange])
  const onUpdate = React.useCallback(handleMassUpdate, [handleMassUpdate])
  const onClickDelete = React.useCallback((model: GridRowSelectionModel) => handleDeleteClick(model), [
    handleDeleteClick,
  ])
  const onClickMassDelete = React.useCallback(
    (model: GridRowSelectionModel) => handleClickMassDelete(model, setSelectedMassData),
    [handleClickMassDelete]
  )
  const onDelete = React.useCallback(() => handleConfirmDelete(selectedTask, setSelectedTask, setIsDeleting), [
    selectedTask,
    handleConfirmDelete,
  ])
  const onMassDelete = React.useCallback(
    (rowSelectionModel, setRowSelectionModel) =>
      handleMassDelete(setSelectedTask, rowSelectionModel, setRowSelectionModel, setSelectedMassData, setIsDeleting),
    [handleMassDelete]
  )

  const onClosePopover = React.useCallback(() => {
    // any extra local cleanup goes here (besides selection, which Wrapper handles)
    setAnchorEl(null)
  }, [])

  const onCloseMassDeletePopover = React.useCallback(() => {
    setIsMassDeleteOpen(false)
    // additional cleanup if needed
  }, [])

  const footerProps = React.useMemo(
    () => ({
      users,
      selectedData: selectedTask,
      selectedMassData,
      setSelectedMassData,
      isLoading: isUsersLoading,
      massUpdateData,
      rowCount: project_tasks?.length ?? 0,
      onChange,
      onUpdate,
      onClickDelete,
      onClickMassDelete,
      onDelete,
      // Wrapper will pass (rowSelectionModel, setRowSelectionModel) here for you:
      onMassDelete,
      isDeleting,
      onClosePopover,
      onCloseMassDeletePopover,
      onClear: clearSelections,
      tasks: project_tasks,
      anchorEl,
      context: 'teamTasks',
      customPosition: footerPosition,
      isMassDeleteOpen,
    }),
    [
      users,
      selectedTask,
      selectedMassData,
      isUsersLoading,
      massUpdateData,
      project_tasks,
      onChange,
      onUpdate,
      onClickDelete,
      onClickMassDelete,
      onDelete,
      onMassDelete,
      isDeleting,
      onClosePopover,
      onCloseMassDeletePopover,
      clearSelections,
      anchorEl,
      footerPosition,
      isMassDeleteOpen,
    ]
  )
  const allRowIds = useMemo(() => {
    if (apiRef.current) return apiRef.current.getAllRowIds()
  }, [apiRef.current])
  const getRowClassNameWrapper = (params: GridRowClassNameParams, rowSelectionModel: GridRowSelectionModel) =>
    getTeamTaskClassNames(params, rowSelectionModel, allRowIds)
  useEffect(() => {
    if (!PK || !SK) {
      if (isDrawerOpen) closeProjectDrawer()
      setSelectedTask(null)
      return
    }

    const selected = treeRows?.find(t =>
      t?.SK?.startsWith('SUBTASK_') ? t?.PK === PK && t?.GSI1PK === SK : t?.SK === SK
    )
    if (!selected) return

    setSelectedTask(prev => (prev?.PK === selected.PK && prev?.SK === selected.SK ? prev : selected))
    if (!isDrawerOpen) openProjectDrawer()
  }, [PK, SK, treeRows, isDrawerOpen, openProjectDrawer, closeProjectDrawer])
  // Close on unmount only
  useEffect(() => {
    return () => {
      closeProjectDrawer()
      setSelectedTask(null)
    }
  }, [])
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity)
  }
  const selectedRowId = useMemo(() => {
    if (!selectedTask) return null
    return `${selectedTask?.PK}::${selectedTask?.SK}`
  }, [selectedTask])

  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={treeRows}
        apiRef={apiRef}
        context={'projects'}
        isExpanded={isExpanded}
        defaultFilterModel={filter_model_projects}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        pinnedColumns={{ left: [], right: [] }}
        onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) =>
          handleTaskRowModelChange(rowSelectionModel)
        }
        onProcessRowUpdate={processTaskRowUpdate}
        isCellEditable={isCellEditable}
        onChangeColumnVisibility={changeColumnVisibility}
        customView={customView}
        setCustomView={setCustomView}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={props => (
          <TaskFooterWrapper {...props} Component={TasksGridFooter} footerProps={footerProps} />
        )}
        DrawerComponent={DrawerWrapper}
        drawerProps={{
          closeDrawer: closeTaskDrawer,
          context: 'projects',
          selectedRowId,
          Component: TaskDrawer,
        }}
        isDrawerOpen={isDrawerOpen}
        onChangeDensity={setDensity}
        treeData={true}
        getRowClassName={getRowClassNameWrapper}
        gridSxProps={taskGridSxProps}
        isLoading={isDeleting || isItemsLoading || isItemsFetching}
        selectedRowId={selectedRowId}
        syncStatus={syncStatus}
        // serverSideMode
        gridContainerSx={{
          position: 'relative',
          pt: pinned ? '60px' : 0.8,
          transition: 'all 0.3s ease', // or .5s, etc.
          width: '100%',
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          zIndex: 1150,
        }}
      />
    </>
  )
}
export default ProjectsPage
